<script setup lang="ts">
import { computed, watch } from 'vue'
import { Cloud, CloudUpload, RotateCcw, X } from '@lucide/vue'
import { Button, InlineAlert, Progress, StateCard, useToast } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { useUploadStore } from '../../stores/upload'
import LibraryOverlay from './library-overlay.vue'
import { useControlSize } from '../../lib/library-layout'

const props = defineProps<{ comic?: Comic }>()
const emit = defineEmits<{ close: [] }>()
const store = useUploadStore()
const toast = useToast()
const controlSize = useControlSize()
const percent = computed(() => store.totalParts ? Math.round(store.completedParts / store.totalParts * 100) : 0)
const published = computed(() => store.server?.status === 'ready')
const canCancel = computed(() => store.hasSession && !published.value && !['publishing', 'binding', 'complete'].includes(store.phase))
const canStart = computed(() => !published.value && ['ready', 'failed'].includes(store.phase) && !store.restartRequired)
// 來源已變更時不自動取消 session，因此續傳與重新上傳兩條出口同時可用。
const canRestart = computed(() => store.restartRequired || (store.sourceChanged && store.hasSession))
const startLabel = computed(() => {
  if (store.sourceChanged) return '仍以既有 session 續傳'
  return store.hasSession ? '繼續上傳' : '開始上傳'
})

watch(() => props.comic?.id, () => {
  if (props.comic) void store.prepare(props.comic)
}, { immediate: true })
watch(() => store.phase, (phase, previous) => {
  if (phase === 'complete' && previous !== 'complete') toast.success('作品已上傳', { description: store.comic?.title })
})
</script>

<template>
  <LibraryOverlay
    :open="!!comic"
    title="上傳到私人雲端"
    :description="`將「${comic?.title ?? ''}」轉成 WebP 後上傳。原始檔案不會修改，未完整驗證前不會出現在雲端書庫。`"
    @close="emit('close')"
  >
    <StateCard v-if="store.phase === 'loading'" title="正在核對上傳來源" message="比對本地檔案與雲端既有的上傳 session…" :actionable="false" />

    <template v-else>
      <InlineAlert v-if="store.error" :tone="store.sourceChanged ? 'warning' : 'error'" class="mb-5">{{ store.error }}</InlineAlert>

      <StateCard
        v-if="store.phase === 'complete'"
        title="已連結雲端作品"
        message="上傳與完整性驗證均已完成，本機作品也已寫入雲端識別碼。"
        :actionable="false"
      >
        <template #icon><Cloud :size="28" aria-hidden="true" /></template>
      </StateCard>
      <StateCard
        v-else-if="store.phase === 'cancelled'"
        title="已取消這次上傳"
        message="本地作品與原始檔案沒有變更。之後可重新開啟此面板建立新 session。"
        :actionable="false"
      >
        <template #icon><X :size="28" aria-hidden="true" /></template>
      </StateCard>

      <template v-else>
        <div v-if="store.progressLabel" class="upload-progress" aria-live="polite">
          <div class="upload-progress-heading"><span>{{ store.progressLabel }}</span><strong>{{ percent }}%</strong></div>
          <Progress :value="store.completedParts" :max="store.totalParts" color="primary" />
          <p>可以關閉面板；轉檔與上傳會在背景繼續，書庫搜尋與其他作品不會被鎖住。</p>
        </div>

        <!-- 面板只講上傳這一件事。分類在書庫的整理對話框或批次分類指派——作品在上傳前就已註冊。 -->
        <div class="upload-scope-note">
          <CloudUpload :size="18" aria-hidden="true" />
          <p>共 {{ comic?.pageCount ?? 0 }} 頁；每頁上傳一張閱讀圖，整本另傳一張封面縮圖。只傳到已驗證的私人 API，不建立公開圖片網址。</p>
        </div>
      </template>
    </template>

    <template #actions>
      <Button outline :size="controlSize" @click="emit('close')">{{ store.busy ? '關閉面板' : '關閉' }}</Button>
      <Button v-if="canCancel" plain color="danger" :size="controlSize" :disabled="store.phase === 'cancelling'" @click="store.cancel">
        {{ store.phase === 'cancelling' ? '正在取消…' : '取消上傳' }}
      </Button>
      <Button v-if="canRestart" :outline="canStart" :color="canStart ? undefined : 'primary'" :size="controlSize" :disabled="store.busy" @click="store.restart">
        <RotateCcw :size="17" aria-hidden="true" />{{ store.sourceChanged ? '放棄並重新上傳' : '重新上傳' }}
      </Button>
      <Button v-if="canStart" color="primary" :size="controlSize" @click="store.start">{{ startLabel }}</Button>
    </template>
  </LibraryOverlay>
</template>
